import React from 'react';
import { Wifi, WifiOff, SignalLow } from 'lucide-react';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';

/**
 * ConnectionBadge
 * Compact pill that shows the user's current network quality.
 * Hidden when connection is healthy on wide screens to reduce clutter;
 * always shown when slow/offline so the provider knows before tapping Generate.
 */
const ConnectionBadge = ({ hideWhenOnline = false, compact = false }) => {
  const { status, effectiveType, rtt, downlink } = useConnectionStatus();

  if (status === 'online' && hideWhenOnline) return null;

  const palette = {
    online: {
      label: 'Online',
      Icon: Wifi,
      cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    },
    slow: {
      label: 'Slow',
      Icon: SignalLow,
      cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30 animate-pulse',
    },
    offline: {
      label: 'Offline',
      Icon: WifiOff,
      cls: 'bg-red-500/15 text-red-400 border-red-500/30',
    },
  }[status];

  const { Icon, label, cls } = palette;
  const tooltipParts = [];
  if (effectiveType) tooltipParts.push(`Type: ${effectiveType}`);
  if (typeof rtt === 'number' && rtt > 0) tooltipParts.push(`RTT: ${rtt}ms`);
  if (typeof downlink === 'number' && downlink > 0) tooltipParts.push(`${downlink}Mbps`);
  const tooltip = tooltipParts.length
    ? `${label} — ${tooltipParts.join(' · ')}`
    : status === 'offline'
      ? 'No internet connection — generate will fail'
      : status === 'slow'
        ? 'Connection is slow — generate may take longer'
        : 'Connection is healthy';

  return (
    <span
      title={tooltip}
      data-testid="connection-badge"
      data-status={status}
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'} rounded-full border font-semibold ${cls}`}
    >
      <Icon className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {status !== 'online' && <span>{label}</span>}
    </span>
  );
};

export default ConnectionBadge;
